import { useEffect, useState } from 'react';
import { C } from '../../theme';
import { updateCardSetting, listQbrTemplates } from '../../db';
import { fmxFetch } from '../../apiClient';
import { GOAL_DEFAULTS, mergeGoals } from '../../qbr/goals';

// Step 4 of the QBR flow — fires /api/qbr-generate, shows progress,
// surfaces the download link. The deck is built server-side from the
// narrative + metrics + grades we've gathered through steps 1–3, plus
// the customer-specific details captured inline on this step (FMXer,
// team contacts, footprint numbers).
//
// Customer details are pre-filled from project.card_settings.QBR.
// customerDetails when present, persist back on save, and reuse across
// every quarterly QBR for this customer. Three of the "Basics" numbers
// (Buildings / Equipment / Resources) plus the Customer URL are
// auto-pulled from the connected FMX site so the FMXer rarely has to
// type them in.

export default function QbrGenerateStep({ project, setup, dataResult, previewResult, currentProfile, onBack }) {
  const [state, setState] = useState('idle'); // 'idle' | 'generating' | 'ready' | 'error'
  const [result, setResult] = useState(null);
  const [error, setError]   = useState(null);
  const [details, setDetails] = useState(() =>
    normalizeDetails(project?.card_settings?.QBR?.customerDetails, project)
  );
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [autoSyncError, setAutoSyncError] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(null);

  // Load the FMXer-selectable templates once on mount. Filtered to
  // is_available; default to whichever is is_active (the admin's
  // chosen default). When zero are available, the generate API falls
  // back to the bundled template so we don't block the FMXer.
  useEffect(() => {
    let cancelled = false;
    listQbrTemplates().then(rows => {
      if (cancelled) return;
      const avail = (rows || []).filter(r => r.is_available);
      setAvailableTemplates(avail);
      const def = avail.find(r => r.is_active) || avail[0];
      if (def) setTemplateId(def.id);
    });
    return () => { cancelled = true; };
  }, []);

  // Pull live counts from FMX on mount so the Basics row reflects the
  // current customer footprint. Only fills fields the FMXer hasn't
  // already typed — manual overrides win.
  useEffect(() => {
    let cancelled = false;
    if (!project?.id || !project?.fmx_connection_verified) return;
    setAutoSyncing(true);
    syncBasicsFromFmx(project.id).then(counts => {
      if (cancelled) return;
      setDetails(prev => ({
        ...prev,
        basics: {
          daysWithFmx:    prev.basics.daysWithFmx    || '',
          buildings:      prev.basics.buildings      || (counts.buildings  != null ? formatCount(counts.buildings)  : ''),
          equipmentItems: prev.basics.equipmentItems || (counts.equipment  != null ? formatCount(counts.equipment)  : ''),
          resources:      prev.basics.resources      || (counts.resources  != null ? formatCount(counts.resources)  : ''),
        },
      }));
      if (counts.error) setAutoSyncError(counts.error);
    }).finally(() => { if (!cancelled) setAutoSyncing(false); });
    return () => { cancelled = true; };
    // eslint-disable-line
  }, [project?.id, project?.fmx_connection_verified]);

  function setField(path, value) {
    setDetails(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let cursor = next;
      for (let i = 0; i < path.length - 1; i++) cursor = cursor[path[i]];
      cursor[path[path.length - 1]] = value;
      return next;
    });
  }

  async function handleGenerate() {
    setState('generating');
    setError(null);
    setResult(null);
    try {
      // Persist the latest details on the project so next quarter's QBR
      // starts pre-filled. Non-fatal if the write fails — the deck can
      // still generate; we'd just lose the autosave.
      if (project?.id) {
        updateCardSetting(project.id, 'QBR', 'customerDetails', details).catch(() => {});
      }
      const res = await fetch('/api/qbr-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: project?.id,
          project:   { id: project?.id, name: project?.name },
          setup,
          dataResult,
          narrative: previewResult?.narrative,
          customerDetails: details,
          templateId,
          headlineGrade: dataResult?.headlineGrade || null,
          userId: currentProfile?.id,
          // Links the qbr_runs row to the Claude narrative call's
          // extraction_runs row for token/cost audit.
          extractionRunId: previewResult?.runId || null,
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(body?.error || `Server returned ${res.status}`);
      }
      setResult(body);
      setState('ready');
    } catch (e) {
      setError(e.message || 'Generation failed.');
      setState('error');
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: C.textMid, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
          Step 4 · Generate
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.navy, marginTop: 2 }}>
          {setup?.quarterLabel} — Final deck
        </div>
        <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
          Fill in the customer-specific details below (saved on the project so you only do this once), then generate the .pptx.
        </div>
      </div>

      <div style={{
        background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10,
        padding: 24,
      }}>
        {state === 'idle' && (
          <IdleView
            project={project} setup={setup} dataResult={dataResult} previewResult={previewResult}
            details={details} setField={setField}
            autoSyncing={autoSyncing} autoSyncError={autoSyncError}
            availableTemplates={availableTemplates}
            templateId={templateId} setTemplateId={setTemplateId}
            onGenerate={handleGenerate}
          />
        )}
        {state === 'generating' && (
          <Centered>
            <Spinner />
            <div style={{ marginTop: 14, fontSize: 13, color: C.textDark, fontWeight: 600 }}>Building deck…</div>
            <div style={{ marginTop: 4, fontSize: 11, color: C.textMid }}>
              Composing 21 slides, uploading to Storage. Usually 3–8 seconds.
            </div>
          </Centered>
        )}
        {state === 'ready' && (
          <ReadyView result={result} setup={setup} onRegenerate={handleGenerate} />
        )}
        {state === 'error' && (
          <ErrorView error={error} onRetry={handleGenerate} />
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
        <button onClick={onBack} style={btnSecondary}>← Back</button>
        <div />
      </div>
    </div>
  );
}

function IdleView({ project, setup, dataResult, previewResult, details, setField, autoSyncing, autoSyncError, availableTemplates, templateId, setTemplateId, onGenerate }) {
  const overallGrade = dataResult?.headlineGrade;
  const wins  = previewResult?.narrative?.exec_summary?.wins?.filter(Boolean) || [];
  const risks = previewResult?.narrative?.exec_summary?.risks?.filter(Boolean) || [];
  const saved = project?.card_settings?.QBR?.customerDetails;
  const prefilled = !!(saved?.fmxerName || saved?.csmName);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 20, alignItems: 'center', marginBottom: 24 }}>
        {overallGrade && (
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: gradeColor(overallGrade), color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 32, fontWeight: 800, boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          }}>{overallGrade}</div>
        )}
        <div>
          <div style={{ fontSize: 11, color: C.textMid, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
            Ready to generate
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.navy, marginTop: 2 }}>
            {project?.name || 'Customer'} · {setup?.quarterLabel}
          </div>
          <div style={{ fontSize: 11, color: C.textMid, marginTop: 4 }}>
            {wins.length} win{wins.length === 1 ? '' : 's'} · {risks.length} risk{risks.length === 1 ? '' : 's'} · {Object.keys(previewResult?.narrative?.metric_callouts || {}).length} metric callouts
          </div>
        </div>
      </div>

      <div style={{
        padding: '8px 12px', borderRadius: 6, fontSize: 11, marginBottom: 16,
        background: prefilled ? C.okBg : '#FFFBEB',
        color:      prefilled ? C.okText : '#92400E',
        border:     prefilled ? `1px solid ${C.okBorder}` : '1px solid #FCD34D',
      }}>
        {prefilled
          ? <>Pre-filled from this project's saved details. Edit anything that's changed and re-save by clicking Generate.</>
          : <>First QBR for this customer — fill in the details below. They'll save on the project so next quarter starts pre-filled.</>}
      </div>

      <DetailsForm
        details={details} setField={setField}
        autoSyncing={autoSyncing} autoSyncError={autoSyncError}
      />

      {availableTemplates.length > 1 && (
        <div style={{
          marginTop: 18, padding: 12,
          border: `1px solid ${C.border}`, borderRadius: 6, background: '#FAFBFC',
          display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 12, alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.navy, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Deck template
            </div>
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
              Pick the layout that fits this customer.
            </div>
          </div>
          <select
            value={templateId || ''}
            onChange={e => setTemplateId(e.target.value || null)}
            style={{
              padding: '7px 10px', fontSize: 13, borderRadius: 5,
              border: `1px solid ${C.border}`, outline: 'none', background: '#fff',
            }}
          >
            {availableTemplates.map(t => (
              <option key={t.id} value={t.id}>
                {t.label || t.original_filename || t.id}{t.is_active ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <button
        onClick={onGenerate}
        style={{
          marginTop: 18,
          padding: '12px 28px', fontSize: 14, fontWeight: 700,
          background: C.orange, color: '#fff', border: 'none', borderRadius: 6,
          cursor: 'pointer',
        }}
      >Save details & generate deck</button>
    </div>
  );
}

function DetailsForm({ details, setField, autoSyncing, autoSyncError }) {
  const basicsHint = autoSyncing
    ? 'Pulling buildings / equipment / resources from FMX…'
    : autoSyncError
      ? `FMX sync failed (${autoSyncError}) — enter manually.`
      : 'Buildings / Equipment / Resources auto-fill from FMX (counts). Days with FMX is manual.';

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <FormSection title="Cover & Open Discussion" subtitle="Used on the cover and the closing Q&A slide">
        <Row>
          <FormField label="FMXer name"       value={details.fmxerName}     onChange={v => setField(['fmxerName'], v)} />
          <FormField label="FMXer title"      value={details.fmxerTitle}    onChange={v => setField(['fmxerTitle'], v)} />
        </Row>
        <Row>
          <FormField label="FMXer email"      value={details.fmxerEmail}    onChange={v => setField(['fmxerEmail'], v)} type="email" />
          <FormField label="Customer URL"     value={details.customerUrl}   onChange={v => setField(['customerUrl'], v)} placeholder="acme.gofmx.com" />
        </Row>
        <FormField label="Cover tagline" value={details.tagline} onChange={v => setField(['tagline'], v)} full />
      </FormSection>

      <FormSection title="Team contacts" subtitle="Three people for the 'Your Team' slide">
        {details.contacts.map((c, i) => (
          <div key={i} style={{
            border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, display: 'grid', gap: 8,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textMid, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Contact {i + 1}
            </div>
            <Row>
              <FormField label="Role label" value={c.role}  onChange={v => setField(['contacts', i, 'role'], v)}  placeholder="PRIMARY CONTACT" />
              <FormField label="Full name"  value={c.name}  onChange={v => setField(['contacts', i, 'name'], v)} />
            </Row>
            <Row>
              <FormField label="Title"      value={c.title} onChange={v => setField(['contacts', i, 'title'], v)} />
              <FormField label="Email"      value={c.email} onChange={v => setField(['contacts', i, 'email'], v)} type="email" />
            </Row>
          </div>
        ))}
      </FormSection>

      <FormSection title="The Basics" subtitle={basicsHint}>
        <Row>
          <FormField label="Days with FMX"   value={details.basics.daysWithFmx}    onChange={v => setField(['basics', 'daysWithFmx'], v)} />
          <FormField label="Buildings"        value={details.basics.buildings}      onChange={v => setField(['basics', 'buildings'], v)} placeholder={autoSyncing ? 'Loading…' : ''} />
        </Row>
        <Row>
          <FormField label="Equipment items" value={details.basics.equipmentItems} onChange={v => setField(['basics', 'equipmentItems'], v)} placeholder={autoSyncing ? 'Loading…' : ''} />
          <FormField label="Resources"        value={details.basics.resources}      onChange={v => setField(['basics', 'resources'], v)} placeholder={autoSyncing ? 'Loading…' : ''} />
        </Row>
      </FormSection>

      <FormSection title="Goals & Outcomes Scorecard" subtitle="Per-project targets for the 5 goals on slide 6. Current values auto-fill from FMX; status (HIT / ON TRACK / WATCH / AT RISK) is computed against the target.">
        <div style={{ display: 'grid', gap: 8 }}>
          {details.goals.map((g, i) => {
            const def = GOAL_DEFAULTS.find(d => d.id === g.id) || {};
            const unitHint = def.unit === 'days' ? 'days' : '%';
            return (
              <div key={g.id} style={{
                border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, display: 'grid', gap: 6,
              }}>
                <Row>
                  <FormField label="Goal label" value={g.label} onChange={v => setField(['goals', i, 'label'], v)} />
                  <FormField label={`Target (${unitHint})`} value={g.target} onChange={v => setField(['goals', i, 'target'], v)} />
                </Row>
              </div>
            );
          })}
        </div>
      </FormSection>
    </div>
  );
}

function normalizeDetails(d, project) {
  const blank = () => ({ initials: '', role: '', name: '', title: '', email: '' });
  return {
    // Read the new fmxer* keys first; fall through to legacy csm* keys
    // so customers with details saved before the rename keep their data.
    fmxerName:   d?.fmxerName   ?? d?.csmName   ?? '',
    fmxerTitle:  d?.fmxerTitle  ?? d?.csmTitle  ?? 'Customer Outcome Manager',
    fmxerEmail:  d?.fmxerEmail  ?? d?.csmEmail  ?? '',
    // Customer URL defaults to the project's connected FMX site URL —
    // they're the same thing. Strip the protocol so the cover slide
    // shows "acme.gofmx.com" instead of "https://acme.gofmx.com/".
    customerUrl: d?.customerUrl || stripSiteUrl(project?.fmx_site_url) || '',
    tagline:     d?.tagline     || 'Building operational excellence together.',
    contacts: [0, 1, 2].map(i => ({
      ...blank(),
      role: ['Primary Contact', 'Maintenance Lead', 'Billing Contact'][i],
      ...(d?.contacts?.[i] || {}),
    })),
    basics: {
      daysWithFmx:    d?.basics?.daysWithFmx    || '',
      buildings:      d?.basics?.buildings      || '',
      equipmentItems: d?.basics?.equipmentItems || '',
      resources:      d?.basics?.resources      || '',
    },
    // Goals scorecard — saved as a small per-project array. Each entry
    // overrides the label/target for one of the 5 standard goals. We
    // merge against GOAL_DEFAULTS on load so the form always shows the
    // canonical 5 rows.
    goals: mergeGoals(d?.goals).map(g => ({
      id: g.id, label: g.label, target: String(g.target ?? ''),
    })),
  };
}

// Pulls the customer's footprint counts from FMX via the standard list
// endpoints. We only need the total — `?limit=1` returns one row and
// the count comes back in the `FMX-Total-Count` header (per the
// OpenAPI). All three fetches run in parallel; partial results are
// fine — whichever didn't return is left blank for manual entry.
async function syncBasicsFromFmx(projectId) {
  const out = { buildings: null, equipment: null, resources: null, error: null };
  const endpoints = [
    ['buildings', '/v1/buildings'],
    ['equipment', '/v1/equipment'],
    ['resources', '/v1/resources'],
  ];
  const results = await Promise.allSettled(endpoints.map(async ([key, ep]) => {
    const res = await fmxFetch({ projectId, endpoint: `${ep}?limit=1`, method: 'GET' });
    if (!res.ok) return [key, null, `${ep} returned ${res.status}`];
    const n = res.headers.get('FMX-Total-Count');
    return [key, n != null ? Number(n) : null, null];
  }));
  const errors = [];
  for (const r of results) {
    if (r.status !== 'fulfilled') { errors.push(r.reason?.message || String(r.reason)); continue; }
    const [key, count, err] = r.value;
    out[key] = count;
    if (err) errors.push(err);
  }
  if (errors.length > 0 && out.buildings == null && out.equipment == null && out.resources == null) {
    out.error = errors[0];
  }
  return out;
}

function formatCount(n) {
  if (n == null || Number.isNaN(Number(n))) return '';
  return Number(n).toLocaleString();
}

function stripSiteUrl(url) {
  if (!url) return '';
  return String(url).replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function FormSection({ title, subtitle, children }) {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.navy, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
      {children}
    </div>
  );
}

function Row({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      {children}
    </div>
  );
}

function FormField({ label, value, onChange, type = 'text', placeholder, full }) {
  return (
    <label style={{
      display: 'grid', gap: 4,
      gridColumn: full ? '1 / -1' : 'auto',
    }}>
      <span style={{ fontSize: 11, color: C.textMid, fontWeight: 600 }}>{label}</span>
      <input
        type={type} value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          padding: '7px 10px', fontSize: 13, borderRadius: 5,
          border: `1px solid ${C.border}`, outline: 'none',
          width: '100%', boxSizing: 'border-box',
        }}
      />
    </label>
  );
}

function ReadyView({ result, setup, onRegenerate }) {
  return (
    <div>
      <div style={{
        padding: 14, borderRadius: 8, marginBottom: 16,
        background: C.okBg, color: C.okText, border: `1px solid ${C.okBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13 }}>Deck ready.</div>
          {result?.warning && (
            <div style={{ fontSize: 11, marginTop: 4 }}>{result.warning}</div>
          )}
        </div>
        {result?.publicUrl && (
          <a
            href={result.publicUrl}
            target="_blank" rel="noopener noreferrer"
            download
            style={{
              padding: '8px 16px', fontSize: 12, fontWeight: 700,
              background: C.navy, color: '#fff', borderRadius: 6, textDecoration: 'none',
            }}
          >↓ Download .pptx</a>
        )}
      </div>
      <div style={{ fontSize: 12, color: C.textMid, marginBottom: 10 }}>
        Saved to Supabase Storage under <code style={inlineCode}>qbr-decks/{result?.storagePath?.split('/')[0]}/…</code>.
        Signed URL is valid for 24 hours; an entry was added to the Past QBRs list.
      </div>
      <button onClick={onRegenerate} style={btnSecondary}>Regenerate</button>
    </div>
  );
}

function ErrorView({ error, onRetry }) {
  return (
    <div>
      <div style={{
        padding: 14, borderRadius: 8, marginBottom: 16,
        background: C.errBg, color: C.errText, border: `1px solid ${C.errBorder}`,
      }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Generation failed</div>
        <div style={{ fontSize: 12 }}>{error}</div>
      </div>
      <button onClick={onRetry} style={{ ...btnSecondary, fontWeight: 600 }}>Try again</button>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div style={{
      minHeight: 220, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', textAlign: 'center',
    }}>{children}</div>
  );
}

function Spinner() {
  // Inline keyframes via a style tag — keeps the spinner self-contained.
  return (
    <div style={{ position: 'relative', width: 36, height: 36 }}>
      <style>{`@keyframes qbrSpin { to { transform: rotate(360deg); } }`}</style>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        border: `3px solid ${C.border}`,
        borderTopColor: C.orange,
        animation: 'qbrSpin 0.9s linear infinite',
      }} />
    </div>
  );
}

function gradeColor(g) {
  return ({ A: '#1A7F4E', B: '#267CE0', C: '#D97706', D: '#B91C1C', F: '#7F1D1D' }[g]) || '#6B7280';
}

const inlineCode = {
  padding: '1px 6px', borderRadius: 3,
  background: '#F3F4F6', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 11,
};
const btnSecondary = {
  padding: '8px 16px', fontSize: 13, fontWeight: 500,
  background: '#fff', color: C.textDark,
  border: `1px solid ${C.border}`, borderRadius: 6, cursor: 'pointer',
};
